import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ReactElement } from 'react'
import styled from 'styled-components'

import { DragIndicatorIcon, WarningIcon } from 'components/Icons'
import { StopIndex } from 'components/Map/StopIndex'
import { LocationDetails } from 'features/RoutingProjects/components/LocationDetails'
import { IDraggedLocation, IRoutingProjectPlanSkippedLocation } from 'features/RoutingProjects/types'
import { WarningIconWrapper } from 'components/WarningNotification'
import Tooltip from 'components/v2/Tooltip'
import { RoutingProjectPlanOptimizationSkippedLocationReason } from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { defineMessages, useIntl } from 'react-intl'
import { isEmpty } from 'lodash'

const ReasonsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ReasonItem = styled.li`
  font-size: 14px;
  font-weight: 400;
  color: #28265d;
`

const skippedLocationReasonMessages = defineMessages({
  [RoutingProjectPlanOptimizationSkippedLocationReason.NoVehicle]: {
    id: 'routingProject.plan.skippedLocations.reasons.noVehicle',
    defaultMessage: 'Please check the constraints.'
  },
  [RoutingProjectPlanOptimizationSkippedLocationReason.CannotBePerformedWithinVehicleDistanceLimit]: {
    id: 'routingProject.plan.skippedLocations.reasons.cannotBePerformedWithinVehicleDistanceLimit',
    defaultMessage: "Cannot be completed within the vehicle's distance limit. Please adjust."
  },
  [RoutingProjectPlanOptimizationSkippedLocationReason.CannotBePerformedWithinVehicleDurationLimit]: {
    id: 'routingProject.plan.skippedLocations.reasons.cannotBePerformedWithinVehicleDurationLimit',
    defaultMessage: "Cannot be completed within the vehicle's duration limit. Please adjust."
  },
  [RoutingProjectPlanOptimizationSkippedLocationReason.DemandExceedsVehicleCapacity]: {
    id: 'routingProject.plan.skippedLocations.reasons.demandExceedsVehicleCapacity',
    defaultMessage: 'Demand exceeds vehicle capacity. Please reduce the load.'
  },
  [RoutingProjectPlanOptimizationSkippedLocationReason.CannotBePerformedWithinVehicleTimeWindows]: {
    id: 'routingProject.plan.skippedLocations.reasons.cannotBePerformedWithinVehicleTimeWindows',
    defaultMessage: 'Please check the constraints.'
  },
  defaultReason: {
    id: 'routingProject.plan.skippedLocations.reasons.default',
    defaultMessage: 'Please check the constraints.'
  }
})

interface ISkippedLocationReasonsProps {
  reasons: RoutingProjectPlanOptimizationSkippedLocationReason[]
}

const SkippedLocationReasons = ({ reasons }: ISkippedLocationReasonsProps): ReactElement => {
  const intl = useIntl()

  if (isEmpty(reasons)) {
    return (
      <ReasonsList>
        <ReasonItem>
          {safeFormatMessage({ intl, messages: skippedLocationReasonMessages, key: 'defaultReason' })}
        </ReasonItem>
      </ReasonsList>
    )
  }

  return (
    <ReasonsList>
      {reasons.map((reason, index) => (
        <ReasonItem key={index}>
          {safeFormatMessage({ intl, messages: skippedLocationReasonMessages, key: reason })}
        </ReasonItem>
      ))}
    </ReasonsList>
  )
}

const Item = styled.li<{ isDragging: boolean }>`
  display: grid;
  align-items: center;
  gap: 8px;
  grid-template-columns: min-content min-content 1fr min-content;
  opacity: ${({ isDragging }): number => (isDragging ? 0.5 : 1)};
`

const DragIndicatorContainer = styled.div`
  cursor: grab;
`

const StyledWarningIconWrapper = styled(WarningIconWrapper)`
  width: 21px;
  min-width: 21px;
  height: 21px;
  min-height: 21px;
`

const StyledTooltip = styled(Tooltip)`
  padding: 8px;
  background-color: #fff;
  border-radius: 4px;
  max-width: 350px;
  text-align: left;
`

interface IOrphanLocationItemProps {
  skippedLocation: IRoutingProjectPlanSkippedLocation
  locationIndex: number
  draggedLocation: IDraggedLocation | null
}

const OrphanLocationItem = ({
  skippedLocation,
  locationIndex,
  draggedLocation
}: IOrphanLocationItemProps): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: skippedLocation.location.id,
    data: {
      locationId: skippedLocation.location.id,
      locationIndex,
      routeIndex: null,
      location: skippedLocation.location,
      wasSkippedLocation: true
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const shownLocationIndex =
    draggedLocation?.location.id === skippedLocation.location.id ? draggedLocation.locationIndex : locationIndex

  return (
    <Item key={skippedLocation.location.id} ref={setNodeRef} style={style} isDragging={isDragging}>
      <DragIndicatorContainer {...attributes} {...listeners}>
        <DragIndicatorIcon />
      </DragIndicatorContainer>
      <StopIndex color="#28265D" bgColor="#E3E3E3">
        {shownLocationIndex + 1}
      </StopIndex>
      <LocationDetails location={skippedLocation.location} />
      <StyledTooltip content={<SkippedLocationReasons reasons={skippedLocation.reasons} />} position="right" hideArrow>
        <StyledWarningIconWrapper>
          <WarningIcon />
        </StyledWarningIconWrapper>
      </StyledTooltip>
    </Item>
  )
}

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

interface IOrphansLocationsListProps {
  skippedLocations: IRoutingProjectPlanSkippedLocation[]
  draggedLocation: IDraggedLocation | null
}

export const OrphansLocationsList = ({
  skippedLocations,
  draggedLocation
}: IOrphansLocationsListProps): ReactElement => {
  const sortableItems = skippedLocations.map((skippedLocation) => skippedLocation.location.id)
  return (
    <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
      <List>
        {skippedLocations.map((skippedLocation, locationIndex) => (
          <OrphanLocationItem
            key={locationIndex}
            skippedLocation={skippedLocation}
            locationIndex={locationIndex}
            draggedLocation={draggedLocation}
          />
        ))}
      </List>
    </SortableContext>
  )
}
